<script setup lang="ts">
import { Dialog, DialogPanel, TransitionRoot, TransitionChild } from '@headlessui/vue';
import CloseIcon from '../.././components/icons/CloseIcon.vue';
import LockIcon from '../.././components/icons/LockIcon.vue';
import InfoIcon from '../.././components/icons/InfoIcon.vue';
import TeamIcon from '../.././components/icons/TeamIcon.vue';
import ContextMenuButton from '../.././components/common/ContextMenu/ContextMenuButton.vue';
import AddTeamList from '../.././components/common/AddTeamList/AddTeamList.vue';
import Dropdown from '../.././components/common/Dropdown/Dropdown.vue';
import DropdownTitleHover from '../.././components/common/Dropdown/DropdownTitleHover.vue';
import { Step } from '../../../types/step';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import { POPUPS_ANIMATION } from '../../../constants/popups';
import CreateWorkspacePopupPosterSide from '@/dashboard/vue/components/popups/CreateWorkspacePopup/CreateWorkspacePopupPosterSide.vue';
import CreatePopupTabs from '@/dashboard/vue/components/popups/CreatePopupTabs/CreatePopupTabs.vue';
import { useCreateWorkspacePopup } from '@/dashboard/vue/popups/CreateWorkspacePopup/useCreateWorkspacePopup';
import VisibilityTypeLabel from '@/dashboard/vue/components/popups/VisibilityTypeLabel.vue';
import DropDownArrowIcon from '@/dashboard/vue/components/icons/DropdownArrowIcon.vue';
import Tooltip from '@/dashboard/vue/components/common/Tooltip.vue';
import WorkspaceRoleTooltip from '@/dashboard/vue/components/tooltips/workspaceRoleTooltip.vue';

const {
  step,
  type,
  showPopup,
  name,
  nameError,
  isCreate,
  buttonText,
  currentUser,
  showPrivate,
  showTeamMenu,
  team,
  tabs,
  onTeamUpdated,
  submitAction,
  closePopup,
  setStep,
} = useCreateWorkspacePopup();
</script>

<template>
  <TransitionRoot :show="showPopup" as="template">
    <Dialog class="fixed bottom-zero left-zero right-zero top-zero z-[5000]" @close="closePopup">
      <!-- The backdrop, rendered as a fixed sibling to the panel container -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.background.enter"
        :enter-from="POPUPS_ANIMATION.background.enterFrom"
        :enter-to="POPUPS_ANIMATION.background.enterTo"
        :leave="POPUPS_ANIMATION.background.leave"
        :leave-from="POPUPS_ANIMATION.background.leaveFrom"
        :leave-to="POPUPS_ANIMATION.background.leaveTo"
      >
        <div class="inset-0 fixed bottom-zero left-zero right-zero top-zero h-full w-full bg-gray-250/60 dark:bg-black/60" aria-hidden="true" />
      </TransitionChild>

      <!-- Full-screen container to center the panel -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.content.enter"
        :enter-from="POPUPS_ANIMATION.content.enterFrom"
        :enter-to="POPUPS_ANIMATION.content.enterTo"
        :leave="POPUPS_ANIMATION.content.leave"
        :leave-from="POPUPS_ANIMATION.content.leaveFrom"
        :leave-to="POPUPS_ANIMATION.content.leaveTo"
      >
        <div class="absolute h-full w-full overflow-auto p-[47px]">
          <div class="flex min-h-full flex-1 items-center justify-center">
            <!-- The actual dialog panel -->
            <DialogPanel class="flex h-[736px] w-[600px] overflow-hidden rounded-16 bg-white text-center dark:bg-gray-950">
              <div class="flex flex-1">
                <CreateWorkspacePopupPosterSide v-if="false" />

                <div class="relative flex flex-1">
                  <button class="absolute right-m top-m outline-none" @click="closePopup()">
                    <CloseIcon class="stroke-black dark:stroke-gray-500" />
                  </button>

                  <div class="flex h-full w-full flex-col items-center">
                    <CreatePopupTabs :current-step="step" :step-changed="setStep" :show-team="showTeamMenu" :tabs="tabs" />

                    <div class="flex w-full flex-1 flex-col items-start">
                      <div v-if="step === Step.Name" class="flex w-full flex-1 flex-col px-[84px]">
                        <div class="flex items-start">
                          <div class="tppr-text-field flex-1" :class="{ error: nameError }">
                            <p class="tppr-text-field__title text-left text-gray-700 dark:text-gray-500">Workspace Name</p>

                            <div class="tppr-text-field__field-container">
                              <input v-model="name" type="text" placeholder="New workspace" />
                            </div>
                          </div>
                        </div>

                        <Divider class="divider-gray-450 dark:divider-gray-750 my-s mt-m w-full" />

                        <div class="flex items-center gap-xs">
                          <span class="text-caption-s text-gray-700">Privacy:</span>

                          <Dropdown body-class="mt-xxs z-[6001] gap-[2px] p-[6px]">
                            <template #title="{ isOpen }: any">
                              <DropdownTitleHover :is-active="isOpen">
                                <div class="flex items-center gap-xs">
                                  <VisibilityTypeLabel :type="type" />
                                  <DropDownArrowIcon class="stroke-black dark:stroke-gray-500" />
                                </div>
                              </DropdownTitleHover>
                            </template>
                            <template #body>
                              <ContextMenuButton v-if="showPrivate" :need-dark="true" :action="() => (type = 'private')">
                                <div class="flex items-center gap-[10px] whitespace-nowrap">
                                  <LockIcon stroke-class="stroke-black dark:stroke-white" fill-class="fill-black dark:fill-white" />
                                  <span class="text-caption-s text-black dark:text-white">Private</span>
                                  <Tooltip>
                                    <template #title>
                                      <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
                                    </template>
                                    <template #body>
                                      <WorkspaceRoleTooltip workspace-type="private" />
                                    </template>
                                  </Tooltip>
                                </div>
                              </ContextMenuButton>
                              <ContextMenuButton :need-dark="true" :action="() => (type = 'team')">
                                <div class="flex items-center gap-[10px] whitespace-nowrap">
                                  <TeamIcon class="stroke-black dark:stroke-white" />
                                  <span class="text-caption-s text-black dark:text-white">Shared with the team</span>
                                  <Tooltip>
                                    <template #title>
                                      <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
                                    </template>
                                    <template #body>
                                      <WorkspaceRoleTooltip workspace-type="team" />
                                    </template>
                                  </Tooltip>
                                </div>
                              </ContextMenuButton>
                            </template>
                          </Dropdown>

                          <Tooltip>
                            <template #title>
                              <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
                            </template>
                            <template #body>
                              <WorkspaceRoleTooltip :workspace-type="type" />
                            </template>
                          </Tooltip>
                        </div>

                        <div class="flex-1"></div>
                      </div>

                      <div v-else-if="step === Step.Team" class="flex w-full flex-1 flex-col px-[84px]">
                        <AddTeamList
                          v-if="currentUser"
                          :is-create="isCreate"
                          :on-team-updated="onTeamUpdated"
                          :current-user="currentUser"
                          :team="team"
                          :type="'workspace'"
                        />
                      </div>

                      <div class="w-full p-xm">
                        <Divider class="divider-gray-450 dark:divider-gray-750 my-s mt-m w-full" />

                        <div class="mt-xm flex w-full justify-between">
                          <button class="button button-ghost h-[48px] max-w-[160px]" @click="() => closePopup()">
                            <span class="button__text">Cancel</span>
                          </button>
                          <button class="button button-primary h-[48px] max-w-[160px]" @click="() => submitAction()">
                            <span class="button__text">{{ buttonText }}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>
